"use client";

import { useState, useEffect } from "react";
import { formatDistanceToNow } from "date-fns";

interface NewsArticle {
  title: string;
  link: string;
  pubDate: Date;
  source: string;
}

interface NewsSidebarProps {
  companyNews: NewsArticle[];
  industryNews: NewsArticle[];
}

function NewsCard({ article }: { article: NewsArticle }) {
  const timeAgo = formatDistanceToNow(new Date(article.pubDate), { addSuffix: true });

  return (
    <a
      href={article.link}
      target="_blank"
      rel="noopener noreferrer"
      className="block p-3 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors"
    >
      <h4 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 line-clamp-2">
        {article.title}
      </h4>
      <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
        {article.source} · {timeAgo}
      </p>
    </a>
  );
}

function NewsSection({ title, articles, accentColor }: { title: string; articles: NewsArticle[]; accentColor: string }) {
  if (articles.length === 0) return null;

  return (
    <div className="mb-4 last:mb-0">
      <h3 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${accentColor}`}>
        {title}
      </h3>
      <div className="space-y-1">
        {articles.map((article, index) => (
          <NewsCard key={index} article={article} />
        ))}
      </div>
    </div>
  );
}

export default function NewsSidebar({ companyNews, industryNews }: NewsSidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Load collapsed state from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem("news-sidebar-collapsed");
    if (stored !== null) {
      setIsCollapsed(stored === "true");
    }
  }, []);

  // Save collapsed state to localStorage
  const toggleCollapsed = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem("news-sidebar-collapsed", String(newState));
  };

  const hasNews = companyNews.length > 0 || industryNews.length > 0;

  if (!hasNews) {
    return null;
  }

  return (
    <div className={`hidden lg:block flex-shrink-0 transition-all duration-200 ${isCollapsed ? "w-12" : "w-72"}`}>
      <div className={`bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 sticky top-8 ${isCollapsed ? "p-2" : "p-4"}`}>
        <div className={`flex items-center justify-between ${isCollapsed ? "mb-0" : "mb-4"}`}>
          {!isCollapsed && (
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">News</h2>
          )}
          <button
            onClick={toggleCollapsed}
            className={`p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200 ${isCollapsed ? "mx-auto" : ""}`}
            title={isCollapsed ? "Show news" : "Hide news"}
          >
            {isCollapsed ? (
              // Chevron right (expand)
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            ) : (
              // Chevron left (collapse)
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            )}
          </button>
        </div>
        {!isCollapsed && (
          <div className="max-h-[calc(100vh-12rem)] overflow-y-auto">
            <NewsSection
              title="Company"
              articles={companyNews}
              accentColor="text-purple-600 dark:text-purple-400"
            />
            <NewsSection
              title="Industry"
              articles={industryNews}
              accentColor="text-blue-600 dark:text-blue-400"
            />
          </div>
        )}
      </div>
    </div>
  );
}
